import { forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { Button, Input } from '@components/ui'
import {
  vehicleService,
  type VehicleRevisionPlanBody,
} from '@services/vehicleService'

type PlanForm = {
  lastRevisionDateStr: string
  lastRevisionKm: string
  revisionIntervalKm: string
  revisionIntervalMonths: string
  currentOdometerKm: string
}

const emptyPlanForm = (): PlanForm => ({
  lastRevisionDateStr: '',
  lastRevisionKm: '',
  revisionIntervalKm: '',
  revisionIntervalMonths: '',
  currentOdometerKm: '',
})

const DEFAULT_DATE_INTERVAL_MONTHS = 12

export type VehicleRevisionPlanPanelHandle = {
  /** Corpo para PATCH; null se o utilizador não quiser atualizar o plano. */
  getRevisionPlanBody: () => VehicleRevisionPlanBody | null
  /** Mensagem de validação quando dados mínimos em falta; null se OK. */
  getValidationError: () => string | null
}

export interface VehicleRevisionPlanPanelProps {
  vehicleId: number
  workshopCompanyId: number
  /** Data sugerida (ex.: resolução da folha de obra), formato YYYY-MM-DD */
  suggestedLastRevisionDate?: string
  /** Km sugerido (ex.: quilometragem nas observações da folha) */
  suggestedLastRevisionKm?: string
  /** Sem botão próprio — o pai guarda no confirmar (ex.: modal Resolvida). */
  embedded?: boolean
}

export const VehicleRevisionPlanPanel = forwardRef<
  VehicleRevisionPlanPanelHandle,
  VehicleRevisionPlanPanelProps
>(function VehicleRevisionPlanPanel(
  {
    vehicleId,
    workshopCompanyId,
    suggestedLastRevisionDate,
    suggestedLastRevisionKm,
    embedded = false,
  },
  ref
) {
  const queryClient = useQueryClient()
  const [planForm, setPlanForm] = useState<PlanForm>(emptyPlanForm)
  const [wantsRevisionControl, setWantsRevisionControl] = useState(false)
  const [wantsToUpdatePlan, setWantsToUpdatePlan] = useState(false)

  const { data: vehicle, isLoading } = useQuery({
    queryKey: ['vehicle', vehicleId],
    queryFn: () => vehicleService.getById(vehicleId),
    enabled: vehicleId > 0,
  })

  useEffect(() => {
    if (!vehicle) return

    const dateFromVehicle = vehicle.lastRevisionDate
      ? String(vehicle.lastRevisionDate).slice(0, 10)
      : ''
    const kmFromVehicle =
      vehicle.lastRevisionKm != null ? String(vehicle.lastRevisionKm) : ''

    const suggestedDate = suggestedLastRevisionDate?.slice(0, 10) ?? ''
    const suggestedKm = suggestedLastRevisionKm?.replace(/\D/g, '') ?? ''

    setWantsRevisionControl(
      vehicle.revisionIntervalKm != null || vehicle.lastRevisionKm != null
    )
    setPlanForm({
      lastRevisionDateStr: dateFromVehicle || suggestedDate,
      lastRevisionKm: kmFromVehicle || suggestedKm,
      revisionIntervalKm:
        vehicle.revisionIntervalKm != null ? String(vehicle.revisionIntervalKm) : '',
      revisionIntervalMonths:
        vehicle.revisionIntervalMonths != null
          ? String(vehicle.revisionIntervalMonths)
          : String(DEFAULT_DATE_INTERVAL_MONTHS),
      currentOdometerKm:
        vehicle.currentOdometerKm != null
          ? String(vehicle.currentOdometerKm)
          : suggestedKm,
    })
  }, [vehicle, suggestedLastRevisionDate, suggestedLastRevisionKm])

  const buildPlanBody = (): VehicleRevisionPlanBody | null => {
    if (!wantsToUpdatePlan) return null

    const body: VehicleRevisionPlanBody = {}

    if (!wantsRevisionControl) {
      if (planForm.lastRevisionDateStr.trim()) {
        body.lastRevisionDate = planForm.lastRevisionDateStr.trim()
      }
      body.revisionIntervalMonths = DEFAULT_DATE_INTERVAL_MONTHS
      body.clearKmBasedPlan = true
    } else {
      if (planForm.lastRevisionDateStr.trim()) {
        body.lastRevisionDate = planForm.lastRevisionDateStr.trim()
      }
      if (planForm.lastRevisionKm.trim()) {
        body.lastRevisionKm = parseInt(planForm.lastRevisionKm, 10)
      }
      if (planForm.revisionIntervalKm.trim()) {
        body.revisionIntervalKm = parseInt(planForm.revisionIntervalKm, 10)
      }
      if (planForm.revisionIntervalMonths.trim()) {
        body.revisionIntervalMonths = parseInt(planForm.revisionIntervalMonths, 10)
      }
      if (planForm.currentOdometerKm.trim()) {
        body.currentOdometerKm = parseInt(planForm.currentOdometerKm, 10)
      }
    }

    return body
  }

  const getValidationError = (): string | null => {
    if (!wantsToUpdatePlan) return null

    if (!planForm.lastRevisionDateStr.trim()) {
      return 'Indique a data da última revisão ou desative a atualização do plano.'
    }

    if (wantsRevisionControl) {
      if (!planForm.lastRevisionKm.trim()) {
        return 'Indique os km na última revisão.'
      }
      if (!planForm.revisionIntervalKm.trim()) {
        return 'Indique o intervalo entre revisões (km).'
      }
    }

    return null
  }

  useImperativeHandle(ref, () => ({
    getRevisionPlanBody: buildPlanBody,
    getValidationError,
  }))

  const saveMutation = useMutation({
    mutationFn: async () => {
      const validationError = getValidationError()
      if (validationError) {
        throw new Error(validationError)
      }
      const body = buildPlanBody()
      if (!body) return
      await vehicleService.patchRevisionPlan(vehicleId, body)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries(['revision-alerts', workshopCompanyId])
      await queryClient.invalidateQueries(['vehicle', vehicleId])
    },
  })

  if (isLoading) {
    return <p className="text-sm text-secondary-500">A carregar plano de revisão…</p>
  }

  return (
    <div className="mt-4 rounded-2xl border border-primary-100 bg-primary-50/40 p-4 sm:p-5">
      <label className="flex cursor-pointer items-start gap-3">
        <input
          type="checkbox"
          className="mt-1 h-4 w-4 rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
          checked={wantsToUpdatePlan}
          onChange={(e) => setWantsToUpdatePlan(e.target.checked)}
        />
        <span>
          <span className="block text-sm font-semibold text-secondary-900">
            Atualizar plano de revisão do veículo
          </span>
          <span className="mt-0.5 block text-xs text-secondary-600">
            {embedded
              ? 'Opcional: registe a última revisão com base nesta folha. Os alertas em Revisões passam a refletir estes dados.'
              : 'Registe a última revisão e os intervalos. Os alertas em Revisões passam a refletir estes dados.'}
          </span>
        </span>
      </label>

      {wantsToUpdatePlan ? (
        <div className="mt-4 space-y-4">
          <label className="block text-sm text-secondary-700">
            <span className="mb-1 block font-medium">
              Data da última inspeção / revisão <span className="text-red-600">*</span>
            </span>
            <input
              type="date"
              className="input w-full"
              value={planForm.lastRevisionDateStr}
              onChange={(e) =>
                setPlanForm((f) => ({ ...f, lastRevisionDateStr: e.target.value }))
              }
            />
          </label>

          <div className="flex items-center justify-between gap-4 rounded-xl border border-secondary-200 bg-white/80 px-4 py-3">
            <span className="text-sm font-medium text-secondary-800">
              Controlar revisões por km?
            </span>
            <div className="flex shrink-0 items-center gap-2">
              <span
                className={`text-xs tabular-nums ${!wantsRevisionControl ? 'font-semibold text-secondary-900' : 'text-secondary-500'}`}
              >
                Não
              </span>
              <button
                type="button"
                role="switch"
                aria-checked={wantsRevisionControl}
                aria-label={
                  wantsRevisionControl
                    ? 'Sim: controlar revisões por km'
                    : 'Não: apenas inspeção por data'
                }
                onClick={() => setWantsRevisionControl((v) => !v)}
                className={`relative h-8 w-14 shrink-0 rounded-full p-0.5 transition-colors duration-200 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 ${
                  wantsRevisionControl ? 'bg-primary-600' : 'bg-secondary-300'
                }`}
              >
                <span
                  className={`absolute top-0.5 h-7 w-7 rounded-full bg-white shadow transition-all duration-300 ease-out ${
                    wantsRevisionControl ? 'left-[calc(100%-1.875rem)]' : 'left-0.5'
                  }`}
                />
              </button>
              <span
                className={`text-xs tabular-nums ${wantsRevisionControl ? 'font-semibold text-secondary-900' : 'text-secondary-500'}`}
              >
                Sim
              </span>
            </div>
          </div>

          <div
            className={`overflow-hidden transition-all duration-300 ease-out motion-reduce:transition-none ${
              wantsRevisionControl
                ? 'max-h-[560px] opacity-100 translate-y-0'
                : 'max-h-0 opacity-0 -translate-y-1 pointer-events-none'
            }`}
          >
            <div className="space-y-4 border-t border-secondary-100 pt-4">
              <Input
                label="Km na última revisão"
                type="number"
                required
                value={planForm.lastRevisionKm}
                onChange={(e) =>
                  setPlanForm((f) => ({ ...f, lastRevisionKm: e.target.value }))
                }
                placeholder="Ex.: 45000"
              />
              <Input
                label="Intervalo entre revisões (km)"
                type="number"
                required
                value={planForm.revisionIntervalKm}
                onChange={(e) =>
                  setPlanForm((f) => ({ ...f, revisionIntervalKm: e.target.value }))
                }
                placeholder="Ex.: 15000"
              />
              <Input
                label="Intervalo entre revisões (meses)"
                type="number"
                value={planForm.revisionIntervalMonths}
                onChange={(e) =>
                  setPlanForm((f) => ({ ...f, revisionIntervalMonths: e.target.value }))
                }
                placeholder="Ex.: 12"
              />
              <Input
                label="Km atual do veículo (opcional)"
                type="number"
                value={planForm.currentOdometerKm}
                onChange={(e) =>
                  setPlanForm((f) => ({ ...f, currentOdometerKm: e.target.value }))
                }
                placeholder="Atualize quando o carro vier à oficina"
              />
            </div>
          </div>
        </div>
      ) : null}

      {!embedded ? (
        <>
          {saveMutation.isError ? (
            <p className="mt-3 text-sm text-red-700">
              {saveMutation.error instanceof Error
                ? saveMutation.error.message
                : 'Não foi possível guardar. Verifique os valores.'}
            </p>
          ) : null}
          {saveMutation.isSuccess ? (
            <p className="mt-3 text-sm text-emerald-700">Plano de revisão guardado.</p>
          ) : null}

          <div className="mt-4 flex justify-end">
            <Button
              type="button"
              variant="primary"
              disabled={saveMutation.isLoading || !wantsToUpdatePlan}
              onClick={() => saveMutation.mutate()}
            >
              {saveMutation.isLoading ? 'A guardar…' : 'Guardar plano de revisão'}
            </Button>
          </div>
        </>
      ) : null}
    </div>
  )
})
